import { useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../context/AuthContext';
import { queryKeys } from '../lib/queryKeys';
import { getUserPreferences, updateUserPreferences } from '../services/dbService';
import type { UserPreferences, UserSettings } from '../types/onboarding';

const DEFAULT_SETTINGS: UserSettings = {};

export function useUserPreferences() {
  const { userId } = useAuth();
  const qc = useQueryClient();
  const key = queryKeys.userPreferences(userId);

  const { data, isLoading } = useQuery<UserPreferences | null>({
    queryKey: key,
    queryFn: getUserPreferences,
    enabled: !!userId,
    staleTime: 5 * 60 * 1000,
  });

  const mutation = useMutation({
    mutationFn: (update: Partial<UserPreferences>) => updateUserPreferences(update),
    onMutate: async (update) => {
      await qc.cancelQueries({ queryKey: key });
      const prev = qc.getQueryData<UserPreferences | null>(key);
      if (prev) {
        qc.setQueryData<UserPreferences | null>(key, {
          ...prev,
          ...update,
          settings: update.settings ? { ...prev.settings, ...update.settings } : prev.settings,
        });
      }
      return { prev };
    },
    onError: (_e, _v, ctx) => {
      if (ctx?.prev !== undefined) qc.setQueryData(key, ctx.prev);
    },
    onSettled: () => qc.invalidateQueries({ queryKey: key }),
  });

  const settings = data?.settings ?? DEFAULT_SETTINGS;

  const updateSettings = useCallback(
    (patch: Partial<UserSettings>) => mutation.mutateAsync({ settings: patch } as any),
    [mutation],
  );

  return {
    preferences: data ?? null,
    settings,
    isLoading,
    isUpdating: mutation.isPending,
    updatePreferences: mutation.mutateAsync,
    updateSettings,
  };
}
